import { useCallback, useEffect, useMemo, useRef, useState, type FocusEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, type UseFormSetValue, type UseFormWatch } from 'react-hook-form'
import { useQueryClient } from '@tanstack/react-query'
import { Reorder } from 'framer-motion'
import { GripVertical, Star, Trash2 } from 'lucide-react'
import { useCreateProfile, useProfile, useSetInterests, useUpdateProfile } from '@/shared/hooks/useProfile'
import { useInterests, type Interest } from '@/shared/hooks/useReference'
import { useAuth } from '@/shared/hooks/useAuth'
import { CitySelect } from '@/shared/components/CitySelect'
import { PhotoUploader } from '@/shared/components/PhotoUploader'
import { apiClient } from '@/shared/api/client'
import { bridge } from '@/shared/bridge'
import { useAppDialog } from '@/shared/dialog/AppDialogContext'
import { interestsCatalogAsClientRows } from '@maximur/shared'

type Step = 'basic' | 'photos' | 'interests' | 'filters'

type ProfilePhotoRow = { id: string; url: string }

interface FormData {
  name: string
  birthDate: string
  gender: 'MALE' | 'FEMALE'
  city: { id: number; name: string }
  bio?: string
  goals: string[]
  heightCm?: number
  education?: string
  occupation?: string
  smoking?: 'NO' | 'SOMETIMES' | 'YES'
  alcohol?: 'NO' | 'SOMETIMES' | 'YES'
  kids?: 'HAVE' | 'WANT' | 'NO' | 'UNDECIDED'
  searchGender: 'MALE' | 'FEMALE' | 'ANY'
  searchAgeFrom: number
  searchAgeTo: number
  searchRadius: number
}

const GOAL_CHOICES: Array<{ value: string; label: string }> = [
  { value: 'SERIOUS', label: 'Серьёзные отношения' },
  { value: 'FRIENDSHIP', label: 'Дружба' },
  { value: 'CHAT', label: 'Общение' },
  { value: 'CASUAL', label: 'Кэжуально' },
  { value: 'UNDECIDED', label: 'Не определился' },
]

function GoalsFieldset({
  watch,
  setValue,
}: {
  watch: UseFormWatch<FormData>
  setValue: UseFormSetValue<FormData>
}) {
  const goals = watch('goals') ?? []

  const toggle = (value: string) => {
    let next: string[]
    if (value === 'UNDECIDED') {
      next = ['UNDECIDED']
    } else {
      const rest = goals.filter((g) => g !== 'UNDECIDED')
      if (rest.includes(value)) {
        const without = rest.filter((g) => g !== value)
        next = without.length ? ['UNDECIDED'] : without
      } else {
        next = [...rest, value]
      }
    }
    setValue('goals', next, { shouldDirty: true })
  }

  return (
    <fieldset className="space-y-2">
      <legend className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
        Цель знакомства
      </legend>
      <p className="text-xs text-gray-500 dark:text-gray-400">Можно выбрать несколько вариантов.</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {GOAL_CHOICES.map((opt) => (
          <label
            key={opt.value}
            className="flex cursor-pointer items-start gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-900/50"
          >
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-accent focus:ring-accent"
              checked={goals.includes(opt.value)}
              onChange={() => toggle(opt.value)}
            />
            <span className="text-sm text-gray-800 dark:text-gray-100">{opt.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}

export function ProfileSetup() {
  const navigate = useNavigate()
  const { user, refreshProfile } = useAuth()
  const showDialog = useAppDialog()
  const [step, setStep] = useState<Step>('basic')
  const [photos, setPhotos] = useState<ProfilePhotoRow[]>([])
  const [profileInitialized, setProfileInitialized] = useState(false)
  const [cityError, setCityError] = useState<string | null>(null)
  const [selectedInterestIds, setSelectedInterestIds] = useState<number[]>([])

  const { register, handleSubmit, watch, setValue, getValues, formState: { errors, isSubmitting } } =
    useForm<FormData>({
      shouldUnregister: false,
      defaultValues: {
        gender: 'MALE',
        goals: ['UNDECIDED'],
        searchGender: 'ANY',
        searchAgeFrom: 18,
        searchAgeTo: 40,
        searchRadius: 50,
      },
    })

  const submitBusyRef = useRef(false)
  const didPrefillNameFromMax = useRef(false)

  const queryClient = useQueryClient()
  const createProfile = useCreateProfile()
  const updateProfile = useUpdateProfile()
  const setInterests = useSetInterests()
  const { data: existingProfile } = useProfile(user?.id || null)
  const { data: interests, isLoading: interestsCatalogLoading } = useInterests()

  const selectedCity = watch('city')

  /** Full catalog: API list, else built-in fallback, plus any interests already on the profile (IDs may differ in DB). */
  const displayInterests = useMemo(() => {
    const apiList = interests ?? []
    const baseCatalog = apiList.length > 0 ? apiList : interestsCatalogAsClientRows()
    const fromProfile =
      existingProfile?.interests
        ?.map((row: { interest?: Interest | null }) => row.interest)
        .filter((x: Interest | null | undefined): x is Interest => !!x?.slug) ?? []
    const bySlug = new Map<string, Interest>()
    for (const i of baseCatalog) {
      bySlug.set(i.slug, i)
    }
    for (const i of fromProfile) {
      if (!bySlug.has(i.slug)) {
        bySlug.set(i.slug, i)
      }
    }
    return Array.from(bySlug.values()).sort((a, b) => a.title.localeCompare(b.title, 'ru'))
  }, [interests, existingProfile])

  const scrollFormControlIntoView = useCallback((e: FocusEvent<HTMLElement>) => {
    const t = e.target
    if (!(t instanceof HTMLElement)) return
    const tag = t.tagName
    if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') return
    if (tag === 'INPUT') {
      const ty = (t as HTMLInputElement).type
      if (
        ty === 'checkbox' ||
        ty === 'radio' ||
        ty === 'hidden' ||
        ty === 'file' ||
        ty === 'button' ||
        ty === 'submit'
      ) {
        return
      }
    }
    requestAnimationFrame(() => {
      t.scrollIntoView({ block: 'center', behavior: 'smooth', inline: 'nearest' })
    })
  }, [])

  const steps: Array<{ key: Step; label: string; number: number }> = [
    { key: 'basic', label: 'Основное', number: 1 },
    { key: 'photos', label: 'Фото', number: 2 },
    { key: 'interests', label: 'Интересы', number: 3 },
    { key: 'filters', label: 'Поиск', number: 4 },
  ]

  const currentStepNumber = steps.find((s) => s.key === step)?.number || 1
  const progress = (currentStepNumber / steps.length) * 100

  useEffect(() => {
    if (existingProfile === undefined) {
      return
    }

    if (existingProfile === null) {
      setProfileInitialized(false)
      setPhotos([])
      setSelectedInterestIds([])
      return
    }

    setValue('name', existingProfile.name || '')
    setValue('birthDate', existingProfile.birthDate?.slice(0, 10) || '')
    setValue('gender', existingProfile.gender || 'MALE')
    setValue('city', {
      id: existingProfile.cityId || 0,
      name: existingProfile.city || '',
    })
    setValue('bio', existingProfile.bio || '')
    const loadedGoals = existingProfile.goals
    if (Array.isArray(loadedGoals) && loadedGoals.length > 0) {
      setValue('goals', loadedGoals as string[])
    } else {
      setValue('goals', ['UNDECIDED'])
    }
    setValue('searchGender', existingProfile.searchGender || 'ANY')
    setValue('searchAgeFrom', existingProfile.searchAgeFrom || 18)
    setValue('searchAgeTo', existingProfile.searchAgeTo || 40)
    setValue('searchRadius', existingProfile.searchRadius || 50)

    const profilePhotos = (existingProfile.photos || []).map((p: { id: string; url: string }) => ({
      id: p.id,
      url: p.url,
    }))
    setPhotos(profilePhotos)

    setProfileInitialized(true)
  }, [existingProfile, setValue])

  useEffect(() => {
    if (existingProfile !== null || existingProfile === undefined) return
    if (didPrefillNameFromMax.current) return
    const fromMax = user?.maxFullName?.trim()
    if (fromMax) {
      setValue('name', fromMax)
      didPrefillNameFromMax.current = true
    }
  }, [existingProfile, user?.maxFullName, setValue])

  useEffect(() => {
    if (existingProfile === undefined || existingProfile === null) {
      if (existingProfile === null) {
        setSelectedInterestIds([])
      }
      return
    }

    if (!existingProfile.interests?.length) {
      setSelectedInterestIds([])
      return
    }

    const apiList = interests ?? []
    const catalog = apiList.length > 0 ? apiList : interestsCatalogAsClientRows()
    const bySlug = new Map(catalog.map((i: Interest) => [i.slug, i.id]))
    const ids: number[] = []
    for (const row of existingProfile.interests as Array<{
      interestId?: number
      interest?: { id?: number; slug?: string } | null
    }>) {
      const slug = row.interest?.slug
      const mapped = slug ? bySlug.get(slug) : undefined
      if (mapped != null) {
        ids.push(mapped)
      } else if (typeof row.interestId === 'number') {
        ids.push(row.interestId)
      } else if (typeof row.interest?.id === 'number') {
        ids.push(row.interest.id)
      }
    }
    setSelectedInterestIds([...new Set(ids)])
  }, [existingProfile, interests])

  const errMessage = (err: unknown) => {
    if (err instanceof Error) return err.message
    if (err && typeof err === 'object' && 'message' in err) return String((err as { message: unknown }).message)
    return String(err)
  }

  const shouldFallbackToUpdate = (err: unknown) => {
    const sc = (err as { statusCode?: number })?.statusCode
    const msg = errMessage(err)
    return (
      sc === 409 ||
      sc === 404 ||
      /unique|already exists|duplicate|P2002/i.test(msg)
    )
  }

  const onSubmit = async (data: FormData) => {
    const merged = { ...getValues(), ...data }
    const profilePayload = {
      ...merged,
      cityId: merged.city?.id,
      city: merged.city?.name ?? '',
    }

    if (step === 'basic') {
      if (!merged.city || !merged.city.id) {
        setCityError('Выбери город из списка')
        return
      }
      if (!merged.goals?.length) {
        showDialog({
          title: 'Цель знакомства',
          message: 'Отметь хотя бы один вариант.',
        })
        return
      }
      try {
        if (profileInitialized) {
          await updateProfile.mutateAsync(profilePayload)
        } else {
          try {
            await createProfile.mutateAsync(profilePayload)
          } catch (inner) {
            if (shouldFallbackToUpdate(inner)) {
              await updateProfile.mutateAsync(profilePayload)
            } else {
              throw inner
            }
          }
          setProfileInitialized(true)
        }
      } catch (err) {
        console.error('Profile init failed:', err)
        showDialog({
          title: 'Не удалось сохранить',
          message: errMessage(err),
        })
        return
      }
      setStep('photos')
      return
    }

    if (step === 'photos') {
      if (photos.length === 0) {
        showDialog({
          title: 'Фото',
          message: 'Добавь хотя бы одно фото.',
        })
        return
      }
      try {
        await apiClient.put('/api/photos/reorder', { ids: photos.map((p) => p.id) })
        await queryClient.invalidateQueries({ queryKey: ['profile'] })
      } catch (err) {
        console.error('Photo reorder failed:', err)
        showDialog({
          title: 'Фото',
          message: 'Не удалось сохранить порядок фото.',
        })
        return
      }
      setStep('interests')
      return
    }

    if (step === 'interests') {
      try {
        await setInterests.mutateAsync(selectedInterestIds)
      } catch (err) {
        console.error('Set interests failed:', err)
        showDialog({
          title: 'Интересы',
          message: 'Не удалось сохранить интересы.',
        })
        return
      }
      setStep('filters')
      return
    }

    try {
      await updateProfile.mutateAsync(profilePayload)
      await queryClient.invalidateQueries({ queryKey: ['profile'] })
      await refreshProfile()

      bridge.haptic('success')
      navigate('/dashboard')
    } catch (err) {
      console.error('Profile creation failed:', err)
      showDialog({
        title: 'Анкета',
        message: errMessage(err),
      })
    }
  }

  const goNext = () => {
    if (submitBusyRef.current || createProfile.isPending || updateProfile.isPending || setInterests.isPending) {
      return
    }
    submitBusyRef.current = true
    void handleSubmit(onSubmit)().finally(() => {
      window.setTimeout(() => {
        submitBusyRef.current = false
      }, 450)
    })
  }

  const removePhoto = async (photo: ProfilePhotoRow) => {
    try {
      await apiClient.delete(`/api/photos/${photo.id}`)
      const next = photos.filter((p) => p.id !== photo.id)
      setPhotos(next)
      if (next.length > 0) {
        await apiClient.put('/api/photos/reorder', { ids: next.map((p) => p.id) })
      }
      await queryClient.invalidateQueries({ queryKey: ['profile'] })
    } catch (err) {
      console.error('Delete photo failed:', err)
      showDialog({
        title: 'Фото',
        message: 'Не удалось удалить фото.',
      })
    }
  }

  const setMainPhoto = async (photoId: string) => {
    const idx = photos.findIndex((p) => p.id === photoId)
    if (idx <= 0) return
    const next = [...photos]
    const [item] = next.splice(idx, 1)
    next.unshift(item)
    setPhotos(next)
    try {
      await apiClient.put('/api/photos/reorder', { ids: next.map((p) => p.id) })
      await queryClient.invalidateQueries({ queryKey: ['profile'] })
    } catch (err) {
      console.error('Reorder failed:', err)
    }
  }

  return (
    <div className="min-h-[100dvh] bg-white dark:bg-gray-950">
      <div className="max-w-2xl mx-auto p-4 pb-44" onFocusCapture={scrollFormControlIntoView}>
        {/* Прогрессбар */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-50">Создай анкету</h1>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {currentStepNumber}/{steps.length}
            </span>
          </div>
          <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-accent transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Шаги */}
        <form
          onSubmit={(e) => {
            e.preventDefault()
            goNext()
          }}
          className="space-y-6"
        >
          {step === 'basic' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Имя
                </label>
                <input
                  {...register('name', { required: 'Обязательное поле' })}
                  type="text"
                  placeholder="Мария"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900/50 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent"
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Дата рождения
                </label>
                <input
                  {...register('birthDate', { required: 'Обязательное поле' })}
                  type="date"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900/50 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent"
                />
                {errors.birthDate && (
                  <p className="text-red-500 text-xs mt-1">{errors.birthDate.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Пол
                </label>
                <div className="flex gap-2">
                  {(['MALE', 'FEMALE'] as const).map((gender) => (
                    <label
                      key={gender}
                      className="flex-1 flex items-center justify-center px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/90 text-gray-800 dark:text-gray-100"
                    >
                      <input
                        {...register('gender')}
                        type="radio"
                        value={gender}
                        className="mr-2"
                      />
                      {gender === 'MALE' ? 'М' : 'Ж'}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Город
                </label>
                <CitySelect
                  value={selectedCity}
                  onChange={(city) => {
                    setValue('city', city)
                    setCityError(null)
                  }}
                  error={cityError}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  О себе
                </label>
                <textarea
                  {...register('bio')}
                  placeholder="Добавь информацию о себе (необязательно)"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900/50 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>

              <GoalsFieldset watch={watch} setValue={setValue} />

            </div>
          )}

          {step === 'photos' && (
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-300">
                Загрузи минимум одно фото. Первое в списке — главное в анкете. Перетащи карточки, чтобы
                поменять порядок.
              </p>
              <PhotoUploader
                onPhotoAdded={(photo) => {
                  setPhotos((prev) => [...prev, { id: photo.id, url: photo.url }])
                  queryClient.invalidateQueries({ queryKey: ['profile'] })
                }}
              />

              {photos.length > 0 && (
                <Reorder.Group
                  axis="y"
                  values={photos}
                  onReorder={setPhotos}
                  className="space-y-2"
                >
                  {photos.map((photo, idx) => (
                    <Reorder.Item
                      key={photo.id}
                      value={photo}
                      className="flex items-stretch gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/70 overflow-hidden touch-pan-y"
                    >
                      <div className="flex items-center px-2 text-gray-400 dark:text-gray-500 cursor-grab active:cursor-grabbing">
                        <GripVertical className="w-5 h-5" />
                      </div>
                      <div className="relative w-20 shrink-0 aspect-square bg-gray-200">
                        <img
                          src={photo.url}
                          alt=""
                          className="w-full h-full object-cover"
                          draggable={false}
                        />
                        {idx === 0 && (
                          <span className="absolute bottom-1 left-1 right-1 text-center text-[10px] font-bold text-white bg-black/60 rounded px-1 py-0.5">
                            Главное
                          </span>
                        )}
                      </div>
                      <div className="flex-1 flex flex-col justify-center gap-1 py-2 pr-2 min-w-0">
                        <div className="flex gap-2 flex-wrap">
                          {idx > 0 && (
                            <button
                              type="button"
                              onClick={() => setMainPhoto(photo.id)}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-accent"
                            >
                              <Star className="w-3.5 h-3.5" />
                              Главное
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => removePhoto(photo)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-red-600"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Удалить
                          </button>
                        </div>
                      </div>
                    </Reorder.Item>
                  ))}
                </Reorder.Group>
              )}
            </div>
          )}

          {step === 'interests' && (
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-300">Выбери свои интересы</p>
              {interestsCatalogLoading && interests === undefined && (
                <p className="text-sm text-gray-500 dark:text-gray-400">Загружаем список интересов…</p>
              )}
              <div className="grid grid-cols-2 gap-2">
                {displayInterests.map((interest) => (
                  <label
                    key={interest.id}
                    className="flex items-center p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/80 text-gray-800 dark:text-gray-100"
                  >
                    <input
                      type="checkbox"
                      checked={selectedInterestIds.includes(interest.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedInterestIds((prev) => [...prev, interest.id])
                        } else {
                          setSelectedInterestIds((prev) => prev.filter((id) => id !== interest.id))
                        }
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-800 dark:text-gray-100">
                      {interest.emoji} {interest.title}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 'filters' && (
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-300 mb-4">Кого ты ищешь?</p>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Пол
                </label>
                <div className="flex gap-2">
                  {(['MALE', 'FEMALE', 'ANY'] as const).map((gender) => (
                    <label
                      key={gender}
                      className="flex-1 flex items-center justify-center px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/90 text-gray-800 dark:text-gray-100"
                    >
                      <input
                        {...register('searchGender')}
                        type="radio"
                        value={gender}
                      />
                      <span className="ml-2 text-sm">
                        {gender === 'MALE' ? 'М' : gender === 'FEMALE' ? 'Ж' : 'Неважно'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    От лет
                  </label>
                  <input
                    {...register('searchAgeFrom', { valueAsNumber: true })}
                    type="number"
                    min="18"
                    max="100"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900/50 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    До лет
                  </label>
                  <input
                    {...register('searchAgeTo', { valueAsNumber: true })}
                    type="number"
                    min="18"
                    max="100"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900/50 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Радиус поиска: {watch('searchRadius')} км
                </label>
                <input
                  {...register('searchRadius', { valueAsNumber: true })}
                  type="range"
                  min="1"
                  max="500"
                  className="w-full"
                />
              </div>
            </div>
          )}

          {/* Кнопки навигации */}
          <div
            className="fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-gray-950/95 border-t border-gray-200 dark:border-gray-700 p-4 z-[100] shadow-[0_-8px_24px_rgba(15,23,42,0.12)] dark:shadow-[0_-8px_24px_rgba(0,0,0,0.45)] backdrop-blur"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
          >
            <div className="max-w-2xl mx-auto flex gap-3">
              {step !== 'basic' && (
                <button
                  type="button"
                  onClick={() => {
                    const currentIdx = steps.findIndex((s) => s.key === step)
                    if (currentIdx > 0) setStep(steps[currentIdx - 1].key)
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors bg-white/80 dark:bg-gray-900/60"
                >
                  ← Назад
                </button>
              )}

              <button
                type="submit"
                disabled={
                  isSubmitting ||
                  createProfile.isPending ||
                  updateProfile.isPending ||
                  setInterests.isPending
                }
                className="flex-1 px-4 py-3 min-h-[52px] rounded-lg font-bold text-white bg-gray-900 dark:bg-indigo-600 shadow-lg shadow-gray-900/25 dark:shadow-indigo-900/40 transition-opacity disabled:opacity-50 touch-manipulation relative z-[101] hover:opacity-95 active:opacity-90"
              >
                {step === 'filters'
                  ? createProfile.isPending || updateProfile.isPending
                    ? 'Сохраняем...'
                    : 'Готово!'
                  : 'Далее →'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
